import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import { alpha, lighten } from '@mui/material/styles';
import { MONTHS } from '../../util/constants';
import { useAgentReports } from '../../hooks/useAgentReports';
import { toast } from 'react-toastify';

const isPlainObject = value => Object.prototype.toString.call(value) === '[object Object]';

const KEY_TRANSLATIONS = {
  success: 'Erfolg',
  status: 'Status',
  booking: 'Buchung',
  bookingNumber: 'Buchungsnummer',
  startTime: 'Startzeit',
  endTime: 'Endzeit',
  entertainment: 'Entertainment',
  resource: 'Raum',
  fromResource: 'Alter Raum',
  toResource: 'Neuer Raum',
  fromStartTime: 'Ursprüngliche Startzeit',
  toStartTime: 'Neue Startzeit',
  fromEndTime: 'Ursprüngliche Endzeit',
  description: 'Beschreibung',
  category: 'Kategorie',
  categories: 'Kategorien',
  duration: 'Dauer',
  validFrom: 'Gültig ab',
  descriptionShort: 'Kurzbeschreibung',
  validTo: 'Gültig bis',
  eligibility: 'Erlaubnis',
  error: 'Fehler',
  targetDate: 'Zieldatum',
  targetTime: 'Zielzeit',
  targetDuration: 'Zieldauer',
  targetEntertainment: 'ZielEntertainment',
  targetResource: 'ZielRaum',
  targetPackage: 'ZielPaket',
  targetUpgrade: 'ZielUpgrade',
  targetUpsell: 'ZielUpsell',
  active: 'Aktiv',
  insurance: 'Ticketversicherung',
  targetParticipantCount: 'ZielTeilnehmer',
  shiftUsed: 'Verschiebung in Anspruch genommen',
  reductionUsed: 'Teilnehmerverminderung in Anspruch genommen',
  publicSelfShiftCount: 'Anzahl Veschiebungen durch Kunden',
  toEndTime: 'Neue Endzeit',
  participantCount: 'Teilnehmer',
  selectedPackage: 'Gewähltes Paket',
  fromPackage: 'Aktuelles Paket',
  toPackage: 'Neues Paket',
  allowed: 'Erlaubt',
  upgradeId: 'Upgrade-ID',
  upsells: 'Upsells',
  name: 'Name',
  count: 'Anzahl',
  dates: 'Verfügbare Tage',
  month: 'Monat',
  year: 'Jahr',
  pricing: 'Preisoption',
  price: 'Preis',
  originalDate: 'Ursprüngliches Datum',
  cancellable: 'Stornierbar',
  windowHours: 'Stornofrist (Std.)',
  hoursUntil: 'Stunden bis Start',
  reason: 'Grund',
  candidateToken: 'Token',
};

const dateTimeFormatter = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const dateFormatter = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'medium',
});

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATE_TIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

const formatKeyLabel = key => {
  if (!key) return '';
  if (KEY_TRANSLATIONS[key]) return KEY_TRANSLATIONS[key];
  if (key.toUpperCase() === key) return key;
  const normalized = key
    .replace(/[_\-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/\s+/g, ' ');
  if (!normalized) return key;
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatDateString = value => {
  if (!value || typeof value !== 'string') return null;
  if (!ISO_DATE_REGEX.test(value) && !ISO_DATE_TIME_REGEX.test(value)) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  if (ISO_DATE_REGEX.test(value) && !value.includes('T')) {
    return dateFormatter.format(date);
  }
  return dateTimeFormatter.format(date);
};

const parseResultValue = value => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return '';
  const looksLikeJson =
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'));
  if (looksLikeJson) {
    try {
      return JSON.parse(trimmed);
    } catch (error) {
      return value;
    }
  }
  return value;
};

const formatPrimitiveValue = (value, key) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'number') {
    if (key === 'month' && value >= 1 && value <= 12) {
      return MONTHS[value - 1] || `${value}`;
    }
    return Number.isNaN(value) ? '-' : `${value}`;
  }
  if (typeof value === 'boolean') {
    return value ? 'Ja' : 'Nein';
  }
  if (typeof value === 'string') {
    const formattedDate = formatDateString(value);
    if (formattedDate) {
      return formattedDate;
    }
    return value.length ? value : '-';
  }
  if (typeof value === 'bigint') {
    return value.toString();
  }
  return String(value);
};

const getActionArgs = action => {
  const args = action?.arguments || action?.args || action?.toolArgs || action?.toolArguments;
  return isPlainObject(args) ? args : null;
};

const pickFirstDefined = (obj, keys) => {
  if (!obj || !keys || !keys.length) return undefined;
  for (const key of keys) {
    const value = obj[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
};

const formatEntityRef = value => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'bigint') return `${value}`;
  if (isPlainObject(value)) {
    const id = pickFirstDefined(value, [
      'id',
      '_id',
      'resourceId',
      'bookingId',
      'upgradeId',
      'token',
    ]);
    const upgradeName =
      value.upgrade && isPlainObject(value.upgrade)
        ? pickFirstDefined(value.upgrade, ['name', 'label', 'title'])
        : undefined;
    const name =
      pickFirstDefined(value, ['name', 'resourceName', 'label', 'title', 'upgradeName']) ||
      upgradeName;
    if (name && id) return `${name} (${id})`;
    if (name) return `${name}`;
    if (id) return `${id}`;
  }
  return value;
};

const buildActionDetails = action => {
  if (!action) return null;

  const args = getActionArgs(action) || {};
  const details = isPlainObject(args) ? { ...args } : {};

  const candidateToken =
    (typeof action?.candidateToken === 'string' && action.candidateToken.trim().length
      ? action.candidateToken.trim()
      : null) ||
    (typeof details.candidateToken === 'string' && details.candidateToken.trim().length
      ? details.candidateToken.trim()
      : null) ||
    null;
  if (candidateToken) details.candidateToken = candidateToken;

  const booking = action?.context?.booking;
  if (isPlainObject(booking)) {
    const bookingNumber = pickFirstDefined(booking, [
      'bookingNumber',
      'booking_number',
      'bookingNo',
    ]);
    if (bookingNumber) details.bookingNumber = bookingNumber;

    const startTime = pickFirstDefined(booking, [
      'startTimeBerlin',
      'startTimeIsoBerlin',
      'startTimeIso',
      'startTime',
    ]);
    const endTime = pickFirstDefined(booking, [
      'endTimeBerlin',
      'endTimeIsoBerlin',
      'endTimeIso',
      'endTime',
    ]);
    if (startTime) details.startTime = startTime;
    if (endTime) details.endTime = endTime;

    const entertainment = pickFirstDefined(booking, ['entertainment', 'entertainmentName']);
    if (entertainment) details.entertainment = entertainment;

    const participantCount = pickFirstDefined(booking, [
      'participantCount',
      'participants',
      'persons',
      'personCount',
    ]);
    if (participantCount !== undefined) details.participantCount = participantCount;

    if (details.resource === undefined && booking.resource !== undefined) {
      details.resource = formatEntityRef(booking.resource);
    }
  }

  if (action?.preflight !== undefined && details.preflight === undefined) {
    details.preflight = action.preflight;
  }

  if (action?.ui?.kind && details.kind === undefined) {
    details.kind = action.ui.kind;
  }

  const entries = Object.entries(details).filter(([, value]) => value !== undefined);
  if (entries.length === 0) return null;
  return Object.fromEntries(entries);
};

const renderPrimitiveValue = (value, keyPrefix, displayKey) => {
  return (
    <Typography
      key={keyPrefix}
      variant='body2'
      sx={{
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontFamily: 'monospace',
      }}
    >
      {formatPrimitiveValue(value, displayKey)}
    </Typography>
  );
};

const renderResultValue = (value, keyPrefix = 'result', depth = 0, displayKey) => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <Typography
          key={`${keyPrefix}-empty-array`}
          variant='body2'
          color='text.secondary'
          sx={{ fontFamily: 'monospace' }}
        >
          -
        </Typography>
      );
    }

    return (
      <Box key={`${keyPrefix}-array`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {value.map((item, index) => (
          <Box
            key={`${keyPrefix}-${index}`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
              pl: depth ? 1.5 : 0,
            }}
          >
            <Typography variant='caption' color='text.secondary'>
              {`#${index + 1}`}
            </Typography>
            {renderResultValue(item, `${keyPrefix}-${index}`, depth + 1, displayKey)}
          </Box>
        ))}
      </Box>
    );
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      return (
        <Typography
          key={`${keyPrefix}-empty-object`}
          variant='body2'
          color='text.secondary'
          sx={{ fontFamily: 'monospace' }}
        >
          -
        </Typography>
      );
    }

    return (
      <Box key={`${keyPrefix}-object`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {entries.map(([entryKey, entryValue]) => (
          <Box
            key={`${keyPrefix}-${entryKey}`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
              pl: depth ? 1.5 : 0,
            }}
          >
            {isPlainObject(entryValue) || Array.isArray(entryValue) ? (
              <>
                <Typography variant='caption' color='text.secondary'>
                  {formatKeyLabel(entryKey)}
                </Typography>
                {renderResultValue(entryValue, `${keyPrefix}-${entryKey}`, depth + 1, entryKey)}
              </>
            ) : (
              <Typography
                variant='body2'
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 0.5,
                  flexWrap: 'wrap',
                  fontFamily: 'monospace',
                }}
              >
                <Box component='span' color='text.secondary' sx={{ fontWeight: 600 }}>
                  {`${formatKeyLabel(entryKey)}:`}
                </Box>
                <Box component='span'>{formatPrimitiveValue(entryValue, entryKey)}</Box>
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  }

  return renderPrimitiveValue(value, keyPrefix, displayKey);
};

const renderActionDetails = action => {
  const details = buildActionDetails(action);
  if (!details) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography variant='caption' color='text.secondary'>
        Details
      </Typography>
      <Box
        sx={{
          borderRadius: 1,
          border: theme => `1px solid ${theme.palette.divider}`,
          backgroundColor: theme =>
            theme.palette.mode === 'light'
              ? alpha(theme.palette.common.black, 0.05)
              : alpha(theme.palette.common.white, 0.04),
          p: 1.25,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          maxHeight: 200,
          overflowY: 'auto',
        }}
      >
        {renderResultValue(details, `${action?.id || 'action'}-details`)}
      </Box>
    </Box>
  );
};

const hasResultData = action => {
  if (!action) return false;
  const hasResult = action.result !== undefined && action.result !== null;
  const hasPreview = action.resultPreview !== undefined && action.resultPreview !== null;
  return hasResult || hasPreview;
};

const getActionResultContent = action => {
  if (!action) return null;
  if (action.result !== undefined) {
    return parseResultValue(action.result);
  }
  if (action.resultPreview !== undefined) {
    return parseResultValue(action.resultPreview);
  }
  return null;
};

const renderActionResult = action => {
  if (!hasResultData(action)) return null;
  const content = getActionResultContent(action);
  return (
    <Box
      sx={{
        borderRadius: 1,
        border: theme => `1px solid ${theme.palette.divider}`,
        backgroundColor: theme =>
          theme.palette.mode === 'light'
            ? alpha(theme.palette.common.black, 0.05)
            : alpha(theme.palette.common.white, 0.04),
        p: 1.25,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        maxHeight: 200,
        overflowY: 'auto',
      }}
    >
      {renderResultValue(content, action?.id || 'result')}
    </Box>
  );
};

const getActionStatusChipProps = status => {
  switch (status) {
    case 'executed':
      return { label: 'Erledigt', color: 'success', variant: 'outlined' };
    case 'failed':
      return { label: 'Fehlgeschlagen', color: 'error', variant: 'outlined' };
    case 'blocked':
      return { label: 'Deaktiviert', color: 'warning', variant: 'outlined' };
    default:
      return { label: 'Bereit', color: 'info', variant: 'outlined' };
  }
};

const AISuggestionCard = ({
  suggestion,
  status = 'idle',
  errorMessage,
  disabled,
  onApply,
  onOwnText,
  onRunAction,
  executingActionId,
  onRetry,
  reportContext,
  forceCollapsed = false,
}) => {
  const hasSuggestion = suggestion && suggestion.text;
  const isPending = status === 'pending';
  const isFailed = status === 'failed';
  const [collapsed, setCollapsed] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const reasonRef = useRef(null);
  const [isReporting, setIsReporting] = useState(false);
  const { reportConversation } = useAgentReports();

  useEffect(() => {
    if (!hasSuggestion) {
      setCollapsed(false);
    }
  }, [hasSuggestion]);

  useEffect(() => {
    if (forceCollapsed) {
      setCollapsed(true);
    }
  }, [forceCollapsed]);

  if (!hasSuggestion && !isPending && !isFailed) return null;

  const isCollapsed = collapsed;

  const handleApply = () => {
    if (disabled || !onApply || !hasSuggestion) return;
    onApply();
  };

  const handleOwnText = () => {
    if (disabled || !onOwnText) return;
    onOwnText();
  };

  const handleRun = action => {
    if (!onRunAction || disabled || action.status === 'blocked') return;
    onRunAction(action);
  };

  const handleOpenReport = () => {
    if (disabled || isPending || !hasSuggestion) return;
    setReportOpen(true);
  };

  const handleCloseReport = () => {
    if (isReporting) return;
    setReportOpen(false);
  };

  const handleSubmitReport = async () => {
    if (disabled || isPending || !hasSuggestion || isReporting) return;

    const reason = reasonRef.current?.value ? String(reasonRef.current.value).trim() : '';

    const kind = reportContext?.kind || 'mail';
    const payload = {
      kind,
      reason,
      mailId: reportContext?.mailId,
      agentSessionToken: reportContext?.agentSessionToken,
      suggestionCacheKey: reportContext?.suggestionCacheKey,
      suggestion,
      context: reportContext?.context,
    };

    setIsReporting(true);
    try {
      await reportConversation(payload);
      toast.success('Vorschlag gemeldet');
      setReportOpen(false);
    } finally {
      setIsReporting(false);
    }
  };

  const renderActions = () => {
    if (!hasSuggestion) return null;

    return (
      Array.isArray(suggestion.actions) &&
      suggestion.actions.map(action => {
        const status = action.status || 'pending';
        const statusChip = getActionStatusChipProps(status);
        const isBlocked = status === 'blocked';
        const isRunning = executingActionId === action.id;
        const canExecute = !disabled && !isBlocked;
        const showReplay = status === 'executed';
        const buttonIcon = isRunning ? (
          <CircularProgress size={18} />
        ) : showReplay ? (
          <ReplayIcon fontSize='small' />
        ) : (
          <PlayArrowIcon fontSize='small' />
        );

        return (
          <Box
            key={action.id}
            sx={{
              borderRadius: 2,
              border: theme => `1px solid ${theme.palette.divider}`,
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant='subtitle2'>{action.label}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip size='small' {...statusChip} />
                <Tooltip
                  title={
                    isBlocked
                      ? 'Im Mail-Modus deaktiviert'
                      : showReplay
                        ? 'Aktion erneut ausführen'
                        : 'Aktion ausführen'
                  }
                >
                  <span>
                    <IconButton
                      size='small'
                      onClick={() => handleRun(action)}
                      disabled={!canExecute || isRunning}
                      color={showReplay ? 'primary' : 'inherit'}
                    >
                      {buttonIcon}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            {action.error && (
              <Typography variant='caption' color='error'>
                Fehler: {action.error}
              </Typography>
            )}
            {renderActionDetails(action)}
            {renderActionResult(action)}
          </Box>
        );
      })
    );
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          borderRadius: 2,
          border: theme => `1px solid ${theme.palette.divider}`,
          backgroundColor: theme =>
            theme.palette.mode === 'light'
              ? lighten(theme.palette.primary.main, 0.94)
              : alpha(theme.palette.common.white, 0.04),
          display: 'flex',
          flexDirection: 'column',
          gap: isCollapsed ? 0 : 1.5,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            rowGap: 1,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: theme =>
              theme.palette.mode === 'light'
                ? lighten(theme.palette.primary.main, 0.88)
                : theme.palette.grey[900],
            p: 1,
            flexShrink: 0,
            // Round only top corners when expanded so the header meets the scroll area flush.
            borderRadius: isCollapsed ? 2 : '8px 8px 0 0',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pl: 0.5,
              pr: 0.5,
              minWidth: 0,
              flex: '1 1 auto',
            }}
          >
            <AutoAwesomeIcon fontSize='small' color='primary' />
            <Typography variant='subtitle2' noWrap sx={{ flexShrink: 0 }}>
              KI-Vorschlag
            </Typography>
            {isPending && <Chip label='In Arbeit' color='info' size='small' />}
            {isFailed && <Chip label='Fehlgeschlagen' color='error' size='small' />}
          </Box>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 'auto' }}
          >
            {hasSuggestion && (
              <>
                <Button
                  size='small'
                  variant='outlined'
                  color='primary'
                  disabled={disabled || isPending}
                  onClick={handleApply}
                >
                  Übernehmen
                </Button>
                <Button
                  size='small'
                  variant='text'
                  color='primary'
                  disabled={disabled || isPending}
                  onClick={handleOwnText}
                >
                  Eigener Text
                </Button>
                <Tooltip title='Vorschlag melden'>
                  <span>
                    <IconButton
                      size='small'
                      onClick={handleOpenReport}
                      disabled={disabled || isPending || !reportContext}
                      color='warning'
                    >
                      <ReportGmailerrorredIcon fontSize='small' />
                    </IconButton>
                  </span>
                </Tooltip>
                <IconButton size='small' onClick={onRetry} disabled={disabled} color='primary'>
                  <AutorenewIcon fontSize='small' />
                </IconButton>
                <IconButton size='small' onClick={() => setCollapsed(prev => !prev)}>
                  {!isCollapsed ? (
                    <ExpandLessIcon fontSize='small' />
                  ) : (
                    <ExpandMoreIcon fontSize='small' />
                  )}
                </IconButton>
              </>
            )}
            {!hasSuggestion && onRetry && (
              <IconButton size='small' onClick={onRetry} disabled={disabled} color='primary'>
                <AutorenewIcon fontSize='small' />
              </IconButton>
            )}
          </Box>
        </Box>
        {!isCollapsed && (
          <Box
            sx={{
              maxHeight: 320,
              overflowY: 'auto',
              overflowX: 'hidden',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              borderBottomLeftRadius: 8,
              borderBottomRightRadius: 8,
            }}
          >
            {isPending && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant='body2' color='text.secondary'>
                  KI-Vorschlag wird erstellt …
                </Typography>
              </Box>
            )}
            {isFailed && (
              <Typography variant='body2' color='error'>
                {errorMessage || 'Der KI-Vorschlag konnte nicht erzeugt werden.'}
              </Typography>
            )}
            {hasSuggestion && (
              <>
                <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                  {suggestion.text}
                </Typography>
                {renderActions()}
              </>
            )}
          </Box>
        )}
      </Box>

      <Dialog open={reportOpen} onClose={handleCloseReport} maxWidth='sm' fullWidth>
        <DialogTitle>Vorschlag melden</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Möchtest du diesen KI-Vorschlag melden? Optional kannst du einen Grund angeben.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={10}
            placeholder='Grund (optional) …'
            inputRef={reasonRef}
            disabled={isReporting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReport} disabled={isReporting}>
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmitReport}
            variant='contained'
            color='warning'
            disabled={isReporting}
          >
            Melden
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AISuggestionCard;
